/**
 * @file src/pages/company-onboarding-wizard/components/Step2Address.jsx
 * @description Wizard step 2 — Saudi Arabia national address fields.
 *
 * What changed from the original:
 *  - Typo fixed: "Plot Identiidcation Number" removed entirely —
 *    plotIdentification is NOT a ZATCA mandatory field per XML standard v1.2
 *  - All field names map directly to ZATCA UBL XML tags (documented inline)
 *  - isFormValid() replaced with useMemo
 *  - aria-describedby added to all inputs for accessibility
 *  - Country field styled consistently as a read-only info block
 *    rather than a disabled Input (cleaner UX signal)
 *
 * ZATCA address rules (BR-KSA-09):
 *  buildingNumber   → cbc:BuildingNumber      — exactly 4 digits (BR-KSA-37)
 *  street           → cbc:StreetName           — mandatory
 *  district         → cbc:CitySubdivisionName  — mandatory
 *  city             → cbc:CityName             — mandatory
 *  countrySubentity → cbc:CountrySubentity     — province / region
 *  postalCode       → cbc:PostalZone           — exactly 5 digits (BR-KSA-66)
 *  country          → cbc:IdentificationCode   — fixed "SA" (BR-09)
 *
 * Rules:
 *  - No API calls, no validation logic
 *  - Errors come from parent via props
 */

import React, { useMemo }   from "react";
import { motion }            from "framer-motion";
import { Flag, HelpCircle, ArrowLeft, ArrowRight } from "lucide-react";
import Input                 from "components/ui/Input";
import Button                from "components/ui/Button";

// ---------------------------------------------------------------------------
// Field config — drives the rendered inputs
// Keeps the JSX below DRY and makes adding/removing fields trivial
// ---------------------------------------------------------------------------

const ADDRESS_FIELDS = [
  {
    key:         "buildingNumber",
    label:       "Building Number",
    placeholder: "1234",
    maxLength:   4,
    hint:        "Exactly 4 digits (ZATCA BR-KSA-37)",
    required:    true,
  },
  {
    key:         "street",
    label:       "Street Name",
    placeholder: "King Fahd Road",
    required:    true,
  },
  {
    key:         "district",
    label:       "District",
    placeholder: "Al Olaya",
    required:    true,
  },
  {
    key:         "plotIdentification",
    label:       "Plot Identification",
    placeholder: "1234",
    maxLength:   4,
    hint:        "Optional — up to 4 digits",
    required:    false,
  },
  {
    key:         "city",
    label:       "City",
    placeholder: "Riyadh",
    required:    true,
  },
  {
    key:         "countrySubentity",
    label:       "Province / Region",
    placeholder: "Riyadh Province",
    maxLength:   30,
    required:    true,
  },
  {
    key:         "postalCode",
    label:       "Postal Code",
    placeholder: "12214",
    maxLength:   5,
    hint:        "Exactly 5 digits (ZATCA BR-KSA-66)",
    required:    true,
  },
];

// ---------------------------------------------------------------------------
// Step2Address
// ---------------------------------------------------------------------------

const Step2Address = ({
  formData,
  updateFormData,
  errors,
  onNext,
  onPrevious,
}) => {
  // ── Form validity gate for Next button ───────────────────────────────────
  // Only required fields block Next — optional fields like plotIdentification
  // can be left blank
  const isValid = useMemo(() =>
    ADDRESS_FIELDS
      .filter(({ required }) => required)
      .every(({ key }) => formData[key]?.trim()),
  [formData]);

  return (
    <div className="space-y-6">

      {/* ── Saudi Arabia notice ───────────────────────────────────────── */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-primary-50 px-4 py-2 rounded-full text-primary-700 mb-2">
          <Flag className="w-4 h-4" />
          <span className="text-sm font-medium">Saudi Arabia Only</span>
        </div>
        <p className="text-gray-600 text-sm">
          ZATCA e-invoicing is currently supported only for Saudi Arabia
        </p>
      </div>

      {/* ── Address fields ────────────────────────────────────────────── */}
      {ADDRESS_FIELDS.map(({ key, label, placeholder, maxLength, hint, required }) => (
        <div key={key} className="space-y-2">
          <label
            htmlFor={`field-${key}`}
            className="block text-sm font-medium text-gray-700"
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>

          <Input
            id={`field-${key}`}
            type="text"
            value={formData[key] ?? ""}
            onChange={(e) => updateFormData(key, e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            className={errors[key] ? "border-error" : ""}
            aria-describedby={
              errors[key] ? `${key}-error` : hint ? `${key}-hint` : undefined
            }
          />

          {errors[key] && (
            <p
              id={`${key}-error`}
              className="text-sm text-error flex items-center gap-1"
              role="alert"
            >
              <HelpCircle className="w-4 h-4 shrink-0" />
              {errors[key]}
            </p>
          )}

          {hint && !errors[key] && (
            <p id={`${key}-hint`} className="text-xs text-gray-500">
              {hint}
            </p>
          )}
        </div>
      ))}

      {/* ── Country — read-only info block ───────────────────────────── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Country
        </label>
        <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
          <Flag className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-sm text-gray-600">Saudi Arabia (SA)</span>
          <span className="ml-auto text-xs text-gray-400">
            Fixed for ZATCA compliance
          </span>
        </div>
        <p className="text-xs text-gray-500">
          Country is automatically set to Saudi Arabia (BR-09)
        </p>
      </div>

      {/* ── Navigation ───────────────────────────────────────────────── */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="min-w-[120px]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <Button
          onClick={onNext}
          disabled={!isValid}
          className="min-w-[120px]"
        >
          Next Step
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="text-center text-sm text-gray-500">
        Step 2 of 3 • Address Information
      </div>
    </div>
  );
};

export default Step2Address;